/* eslint-disable @typescript-eslint/no-unused-vars */
// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Notification from 'antd/lib/notification';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Radio from 'antd/lib/radio';
import Alert from 'antd/lib/alert';
import Form from 'antd/lib/form';
import TargetStorageField from 'components/storage/target-storage-field';
import { CombinedState, StorageLocation } from 'reducers';
import { exportActions, exportDatasetAsync } from 'actions/export-actions';
import {
    Dumper, Job, Project, Storage, StorageData, Task,
    getCore,
} from 'cvat-core-wrapper';
import Switch from 'antd/lib/switch';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import { Row, Col } from 'antd/lib/grid';
import { t } from 'utils/i18n';

type FrameScope = 'current' | 'range' | 'all';

type FormValues = {
    frameScope: FrameScope;
    frameRange: [number, number];
    saveImages: boolean;
    floorsAsLevels: boolean;
    autoLP: boolean;
    forAddon: boolean;
    sceneMode: string;
    sceneName: string | undefined;
    customName: string | undefined;
    targetStorage: StorageData;
    useProjectTargetStorage: boolean;
};

const switchOptions: { name: keyof FormValues; label: string }[] = [
    { name: 'floorsAsLevels', label: t('Treat Floors as Levels') },
    { name: 'saveImages', label: t('Save images') },
    { name: 'autoLP', label: t('Use AutoLP') },
    { name: 'forAddon', label: t('Export for Addon') },
];

const initialValues: Partial<FormValues> = {
    saveImages: false,
    floorsAsLevels: false,
    autoLP: false,
    forAddon: false,
    sceneMode: 'VR',
    sceneName: undefined,
    frameScope: 'all',
    customName: undefined,
    targetStorage: {
        location: StorageLocation.LOCAL,
        cloudStorageId: undefined,
    },
    useProjectTargetStorage: true,
};

function FrameRangeField({ start, stop }: { start: number; stop: number }): JSX.Element {
    return (
        <Space align='baseline' className='cvat-modal-export-blender-frame-range'>
            <Form.Item
                name={['frameRange', 0]}
                dependencies={[['frameRange', 1]]}
                rules={[
                    { required: true, message: t('Specify the first frame') },
                    ({ getFieldValue }) => ({
                        validator: (_, value: number) => (
                            value <= getFieldValue(['frameRange', 1]) ?
                                Promise.resolve() :
                                Promise.reject(new Error(t('The first frame is after the last one')))
                        ),
                    }),
                ]}
            >
                <InputNumber min={start} max={stop} precision={0} />
            </Form.Item>
            <Text>{t('to')}</Text>
            <Form.Item name={['frameRange', 1]} rules={[{ required: true, message: t('Specify the last frame') }]}>
                <InputNumber min={start} max={stop} precision={0} />
            </Form.Item>
        </Space>
    );
}

function ExportBlenderModal(props: StateToProps): JSX.Element {
    const { instance, currentFrame } = props;
    const job = instance instanceof Job ? instance : null;
    const multiFrameJob = !!job && job.startFrame !== job.stopFrame;

    const [instanceType, setInstanceType] = useState('');

    const [useDefaultTargetStorage, setUseDefaultTargetStorage] = useState(true);
    const [form] = Form.useForm();
    const [targetStorage, setTargetStorage] = useState<StorageData>({
        location: StorageLocation.LOCAL,
    });
    const [defaultStorageLocation, setDefaultStorageLocation] = useState(StorageLocation.LOCAL);
    const [defaultStorageCloudId, setDefaultStorageCloudId] = useState<number>();
    const [helpMessage, setHelpMessage] = useState('');
    const [projectFolderAttached, setProjectFolderAttached] = useState<boolean | null>(null);
    const dispatch = useDispatch();
    // instanceType stays English: classNames are built from it
    const [kind, id = ''] = instanceType.split(' #');
    const target = t(`${kind} #{id}`, { id });

    useEffect(() => {
        // a task without a project folder exports from the bundled assets; say so up front,
        // and let the server propose Project_Name from the folder and the task
        setProjectFolderAttached(null);
        let taskId: number | null = null;
        if (instance instanceof Task) taskId = instance.id;
        if (instance instanceof Job) taskId = instance.taskId;
        if (taskId === null) return;
        const core = getCore();
        core.server.request(`${core.config.backendAPI}/tasks/${taskId}/project_source`, { method: 'GET' })
            .then((response: any) => setProjectFolderAttached(response.data.length > 0))
            .catch(() => setProjectFolderAttached(null));
        core.server.request(`${core.config.backendAPI}/tasks/${taskId}/scene_name`, { method: 'GET' })
            .then((response: any) => form.setFieldsValue({ sceneName: response.data.scene_name }))
            .catch(() => {});
        // the mode saved in the editor's Scene panel
        core.server.request(`${core.config.backendAPI}/tasks/${taskId}/scene`, { method: 'GET' })
            .then((response: any) => form.setFieldsValue({ sceneMode: response.data.mode }))
            .catch(() => {});
    }, [instance, form]);

    useEffect(() => {
        if (instance instanceof Project) {
            setInstanceType(`project #${instance.id}`);
        } else if (instance instanceof Task || instance instanceof Job) {
            if (instance instanceof Task) {
                setInstanceType(`task #${instance.id}`);
            } else {
                setInstanceType(`job #${instance.id}`);
            }
        }
    }, [instance]);

    useEffect(() => {
        if (instance) {
            setDefaultStorageLocation(instance.targetStorage.location);
            setDefaultStorageCloudId(instance.targetStorage.cloudStorageId);
        }
    }, [instance]);

    useEffect(() => {
        const where = defaultStorageLocation ? defaultStorageLocation.split('_')[0] : 'local';
        setHelpMessage(
            t(`Export to ${where} storage {id}`, { id: defaultStorageCloudId ? `№${defaultStorageCloudId}` : '' }),
        );
    }, [defaultStorageLocation, defaultStorageCloudId]);

    const closeModal = (): void => {
        setUseDefaultTargetStorage(true);
        setTargetStorage({ location: StorageLocation.LOCAL });
        form.resetFields();
        dispatch(exportActions.closeExportBlenderModal(instance));
    };

    const handleExport = useCallback(
        (values: FormValues): void => {
            let frames: string | undefined;
            let singleFile = job?.startFrame === job?.stopFrame;
            if (multiFrameJob && values.frameScope === 'current') {
                frames = `${currentFrame}`;
                singleFile = true;
            } else if (multiFrameJob && values.frameScope === 'range') {
                const [first, last] = values.frameRange;
                frames = first === last ? `${first}` : `${first}-${last}`;
                singleFile = first === last;
            }
            let customName;
            if (values.customName) {
                customName = singleFile ? `${values.customName}.blend` : `${values.customName}.zip`;
            }
            dispatch(
                exportDatasetAsync(
                    instance,
                    'Blender 1.0',
                    values.saveImages,
                    useDefaultTargetStorage,
                    useDefaultTargetStorage ?
                        new Storage({
                            location: defaultStorageLocation,
                            cloudStorageId: defaultStorageCloudId,
                        }) :
                        new Storage(targetStorage),
                    customName,
                    values.floorsAsLevels,
                    values.autoLP,
                    values.forAddon,
                    singleFile,
                    values.sceneMode,
                    values.sceneName,
                    frames,
                ),
            );
            closeModal();
            const resource = 'Blender';
            Notification.info({
                message: t(`${resource} export started`),
                description: t(
                    `${resource} export was started for {target}. ` +
                        `Download will start automatically as soon as the ${resource} is ready.`,
                    { target },
                ),
                className: `cvat-notification-notice-export-${instanceType.split(' ')[0]}-start`,
            });
        },
        [
            instance, instanceType, target, multiFrameJob, currentFrame, useDefaultTargetStorage,
            defaultStorageLocation, defaultStorageCloudId, targetStorage,
        ],
    );

    return (
        <Modal
            title={<Text strong>{t('Export Blender files from {target}', { target })}</Text>}
            visible={!!instance}
            onCancel={closeModal}
            onOk={() => form.submit()}
            className={`cvat-modal-export-${instanceType.split(' ')[0]}`}
            destroyOnClose
        >
            <Form
                name='Export Blender files'
                form={form}
                layout='vertical'
                initialValues={{
                    ...initialValues,
                    frameRange: job ? [job.startFrame, job.stopFrame] : undefined,
                }}
                onFinish={handleExport}
            >
                {projectFolderAttached === false && (
                    <Alert
                        className='cvat-modal-export-no-project-folder'
                        type='warning'
                        showIcon
                        message={t('No project folder is attached to this task: the export builds from the bundled assets.')}
                    />
                )}
                {multiFrameJob && (
                    <>
                        <Form.Item name='frameScope' label={<Text strong>{t('Frames')}</Text>}>
                            <Radio.Group className='cvat-modal-export-blender-frame-scope'>
                                <Radio value='current'>{t('Current frame ({n})', { n: currentFrame })}</Radio>
                                <Radio value='range'>{t('Range')}</Radio>
                                <Radio value='all'>{t('All frames')}</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev.frameScope !== next.frameScope}>
                            {({ getFieldValue }) => getFieldValue('frameScope') === 'range' && (
                                <FrameRangeField start={job.startFrame} stop={job.stopFrame} />
                            )}
                        </Form.Item>
                    </>
                )}
                <Row gutter={[16, 8]} className='cvat-modal-export-blender-switches'>
                    {switchOptions.map(({ name, label }) => (
                        <Col span={12} key={name}>
                            <Space align='center'>
                                <Form.Item name={name} valuePropName='checked' noStyle>
                                    <Switch />
                                </Form.Item>
                                <Text strong>{label}</Text>
                            </Space>
                        </Col>
                    ))}
                </Row>
                <Form.Item name='sceneMode' label={<Text strong>{t('Asset set')}</Text>}>
                    <Select className='cvat-modal-export-scene-mode'>
                        <Select.Option value='VR'>VR</Select.Option>
                        <Select.Option value='FP'>{t('Floorplan')}</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    label={<Text strong>{t('Scene name')}</Text>}
                    name='sceneName'
                    tooltip={t('Example: MODON001_Hudayriyat_S007 or ALDAR039_NYU_LVL1-3. The export appends _FP_link / _VR_link (or _unlink) and names the LP file _LP.')}
                >
                    <Input placeholder='Project_Name' className='cvat-modal-export-scene-name-input' />
                </Form.Item>
                <Form.Item label={<Text strong>{t('Custom name')}</Text>} name='customName'>
                    <Input
                        placeholder={t('Custom name for archive')}
                        className='cvat-modal-export-filename-input'
                    />
                </Form.Item>
                <Space>
                    <TargetStorageField
                        instanceId={instance ? instance.id : null}
                        switchDescription={t('Use default settings')}
                        switchHelpMessage={helpMessage}
                        useDefaultStorage={useDefaultTargetStorage}
                        storageDescription={t('Specify target storage for export Blender files')}
                        locationValue={targetStorage.location}
                        onChangeUseDefaultStorage={(value: boolean) => setUseDefaultTargetStorage(value)}
                        onChangeStorage={(value: StorageData) => setTargetStorage(value)}
                        onChangeLocationValue={(value: StorageLocation) => {
                            setTargetStorage({ location: value });
                        }}
                    />
                </Space>
            </Form>
        </Modal>
    );
}

interface StateToProps {
    dumpers: Dumper[];
    instance: Project | Task | Job | null;
    current: string[];
    currentFrame: number;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const { instanceType } = state.export;
    const instance = !instanceType ?
        null :
        state.export[`${instanceType}s` as 'projects' | 'tasks' | 'jobs'].blender.modalInstance;

    return {
        instance,
        current: !instanceType ?
            [] :
            state.export[`${instanceType}s` as 'projects' | 'tasks' | 'jobs'].blender.current[instance.id],
        dumpers: state.formats.annotationFormats.dumpers,
        currentFrame: state.annotation.player.frame.number,
    };
}

export default connect(mapStateToProps)(ExportBlenderModal);
